<template>
  <div
    v-click-outside="emitClose"
    v-tooltip="tooltip"
    class="sf-dropdown2 tw-rounded-lg tw-content-center"
    :class="dropdownClasses"
    data-testid="dropdown"
    v-bind="$attrs"
    @click.stop.prevent="emitClicked"
  >
    <div :class="selectWrapperClassnames" data-testid="dropdown-wrapper">
      <div
        class="sf-dropdown2-select tw-rounded-lg"
        :class="selectClassNames"
        data-testid="dropdown-select"
        @click="emitTriggerClicked"
      >
        <slot name="trigger" />
      </div>
    </div>
    <slot name="actions" />
    <div :class="dropdownMenuClassnames">
      <slot name="menu" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  variants,
  drawerSizes,
  triggerSizes,
  drawerDirections,
} from './constants'
import { computed, useSlots } from 'vue'

const slots = useSlots()

defineOptions({
  name: 'SfDropdownMenu',
})

interface DropdownProps {
  dropdownId: string
  isFullWidth?: boolean
  isDisabled?: boolean
  isOpen?: boolean
  noOptionsStyling?: boolean
  // Cap the menu at the trigger width so a long option can't widen the dropdown;
  // labels wrap (and long tokens break) within that width instead of pushing the
  // menu out. Off by default so existing dropdowns keep sizing to their content.
  constrainMenuToTriggerWidth?: boolean
  triggerSize?: string
  drawerSize?: string
  variant?: string
  alignOption?: string
  drawerDirection?: string
  isInsideOtherInput?: boolean
  unstyledTrigger?: boolean
  tooltip?: string
  selectClassNames?: string[]
  menuClassNames?: string[]
}

const props = withDefaults(defineProps<DropdownProps>(), {
  variant: variants.neutral,
  drawerSize: drawerSizes.default,
  triggerSize: triggerSizes.default,
  drawerDirection: drawerDirections.right,
  alignOption: 'l',
  tooltip: '',
  selectClassNames: () => [],
  menuClassNames: () => [],
})

const emit =
  defineEmits<{
    'dropdown-closed': [dropdownId: string]
    'dropdown-clicked': [dropdownId: string]
    'dropdown-trigger-clicked': [dropdownId: string]
  }>()

const selectClassNames = computed(() => {
  return [
    {
      'sf-dropdown2-select-padding-small':
        props.triggerSize === triggerSizes.small && !props.unstyledTrigger,
      'tw-px-2.5 tw-py-1 tw-text-xs tw-leading-4':
        props.triggerSize === triggerSizes.extraSmall && !props.unstyledTrigger,
      'tw-p-0': props.unstyledTrigger,
      [`sf-dropdown2-select--${props.variant}`]: props.variant,
      'tw-py-1.5':
        !props.unstyledTrigger &&
        props.isInsideOtherInput &&
        props.triggerSize !== triggerSizes.extraSmall,
      'tw-py-[9px]':
        !props.unstyledTrigger &&
        !props.isInsideOtherInput &&
        props.triggerSize !== triggerSizes.extraSmall,
    },
    ...props.selectClassNames,
  ]
})

const selectWrapperClassnames = computed(() => {
  return {
    'sf-dropdown2-wrapper':
      !props.unstyledTrigger && props.triggerSize !== triggerSizes.extraSmall,
    'sf-dropdown2-wrapper--open': props.isOpen,
    [`sf-dropdown2-wrapper--${props.variant}`]: props.variant,
    'tw-w-full': props.isFullWidth,
  }
})

const dropdownClasses = computed(() => {
  return {
    'sf-disabled': props.isDisabled,
    [`sf-dropdown2-${props.variant}`]: props.variant,
    [`sf-align-${props.alignOption}`]: props.alignOption,
    'tw-w-full': props.isFullWidth,
    'sf-dropdown2-with-actions tw-inline-flex': !!slots.actions,
  }
})

const dropdownMenuClassnames = computed(() => {
  return [
    {
      'sf-dropdown2-menu': !props.noOptionsStyling,
      'tw-mt-2': !props.noOptionsStyling,
      'sf-dropdown2-menu--open': props.isOpen,
      'tw-flex tw-flex-col tw-top-8': !!slots.actions && props.isOpen,
      'sf-dropdown2-menu--large': props.drawerSize === drawerSizes.large,
      'sf-dropdown2-menu--constrained': props.constrainMenuToTriggerWidth,
      [`open-drawer-${props.drawerDirection}`]: props.drawerDirection,
    },
    ...props.menuClassNames,
  ]
})

const emitClose = () => {
  if (props.isOpen && !props.isDisabled) {
    emit('dropdown-closed', props.dropdownId)
  }
}

const emitClicked = () => {
  if (!props.isDisabled) {
    emit('dropdown-clicked', props.dropdownId)
  }
}

const emitTriggerClicked = () => {
  if (!props.isDisabled) {
    emit('dropdown-trigger-clicked', props.dropdownId)
  }
}
</script>
<style lang="scss">
.sf-dropdown2-select-padding-small {
  padding: 2px 26px 2px 8px;
}

/* Invalid dropdown (select-class-names: ['sf-form-warning'], e.g. via a
   hasError prop). The trigger is a <div>, not a real .sf-form-select, so
   ui-kit's `.sf-form-warning.sf-form-select` rule never reaches it — apply the
   same warning palette to the trigger here (kept on :hover so it doesn't revert
   to the neutral hover border). */
.sf-dropdown2-select.sf-form-warning,
.sf-dropdown2-select.sf-form-warning:hover {
  border-color: theme('colors.sf-warning.light-1');
  background-color: theme('colors.sf-warning.subtle-2');
  color: theme('colors.sf-warning.DEFAULT');
}

.sf-dropdown2-menu--constrained {
  max-width: 100%;
}

/* Wrap (and break long tokens) so the row fits the capped width and the
   selected-item checkmark isn't pushed out of view. The min-width reset drops
   the ui-kit's `.sf-dropdown2-item { min-width: 160px }` floor, which otherwise
   keeps the item wider than a narrow trigger and clips the checkmark. */
.sf-dropdown2-menu--constrained .sf-dropdown2-item {
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: normal;
}

.open-drawer-left {
  right: 0;
}

.open-drawer-right {
  left: 0;
}

.sf-dropdown2-with-actions {
  .sf-dropdown2-select {
    border-radius: 0.5rem 0 0 0.5rem;
  }

  .sf-dropdown2-trash {
    box-shadow: 0 1px 0 0 #e6e7e8;
  }

  .sf-dropdown2-dismiss {
    border-radius: 0 0.5rem 0.5rem 0;
    box-shadow: 0 1px 0 0 #e6e7e8;
  }

  .sf-dropdown2-menu {
    min-width: initial;
  }

  .sf-dropdown2-item {
    align-items: center;
  }
}
</style>
